import { createWebHistory, createRouter, type RouteComponent, type RouteLocationNormalized } from 'vue-router';

const CompanyCockpitPage = (): Promise<RouteComponent> => import('@/components/pages/CompanyCockpitPage.vue');

const QualityAssurance = (): Promise<RouteComponent> => import('@/components/pages/QualityAssurance.vue');
const SearchCompaniesForFrameworkData = (): Promise<RouteComponent> =>
  import('@/components/pages/SearchCompaniesForFrameworkData.vue');
const NoContentFound = (): Promise<RouteComponent> => import('@/components/pages/NoContentFound.vue');
const ApiKeysPage = (): Promise<RouteComponent> => import('@/components/pages/ApiKeysPage.vue');
const SingleDataRequest = (): Promise<RouteComponent> => import('@/components/pages/SingleDataRequest.vue');
const ViewFrameworkData = (): Promise<RouteComponent> => import('@/components/pages/ViewFrameworkData.vue');
const DocumentOverview = (): Promise<RouteComponent> => import('@/components/pages/DocumentOverview.vue');
const DatasetOverview = (): Promise<RouteComponent> => import('@/components/pages/DatasetOverview.vue');
const MyDataRequestsOverview = (): Promise<RouteComponent> => import('@/components/pages/MyDataRequestsOverview.vue');

const PortfolioOverview = (): Promise<RouteComponent> => import('@/components/pages/PortfolioOverview.vue');
const SharedPortfolioOverview = (): Promise<RouteComponent> => import('@/components/pages/SharedPortfolioOverview.vue');
const ViewDataRequestPage = (): Promise<RouteComponent> => import('@/components/pages/ViewDataRequestPage.vue');

const UnsubscribeFromMailsPage = (): Promise<RouteComponent> =>
  import('@/components/pages/UnsubscribeFromMailsPage.vue');
const UploadFormWrapper = (): Promise<RouteComponent> => import('@/components/pages/UploadFormWrapper.vue');
const ChooseCompanyForFrameworkDataUpload = (): Promise<RouteComponent> =>
  import('@/components/pages/ChooseCompanyForFrameworkDataUpload.vue');
const AdminRequestsOverview = (): Promise<RouteComponent> => import('@/components/pages/AdminAllRequestsOverview.vue');

const ChooseFrameworkForDataUpload = (): Promise<RouteComponent> =>
  import('@/components/pages/ChooseFrameworkForDataUpload.vue');
const DatasetReviewOverview = (): Promise<RouteComponent> => import('@/components/pages/DatasetReviewOverview.vue');
import PlatformRedirect from '@/components/pages/PlatformRedirect.vue';
import KeycloakRedirect from '@/components/pages/KeycloakRedirect.vue';

const routes = [
  {
    path: '/companies/choose',
    name: 'Choose Company',
    component: ChooseCompanyForFrameworkDataUpload,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/companies/:companyID/frameworks/upload',
    props: true,
    name: 'Choose Framework',
    component: ChooseFrameworkForDataUpload,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyID/frameworks/:frameworkType/upload`,
    props: true,
    name: `Upload framework data`,
    component: UploadFormWrapper,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/companies',
    name: 'Search Companies for Framework Data',
    component: SearchCompaniesForFrameworkData,
    meta: {
      initialTabId: 'companies',
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId`,
    props: true,
    name: 'Company Cockpit',
    component: CompanyCockpitPage,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/companies/:companyId/frameworks/:dataType`,
    props: true,
    name: 'Company framework data view page',
    component: ViewFrameworkData,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId/frameworks/:dataType/:dataId`,
    props: true,
    name: 'Company framework data view page for specific data ID',
    component: ViewFrameworkData,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId/frameworks/:dataType/reportingPeriods/:reportingPeriod`,
    props: true,
    name: 'Company framework data for specific reporting period',
    component: ViewFrameworkData,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId/documents`,
    props: true,
    name: 'Document Overview',
    component: DocumentOverview,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId/users`,
    props: true,
    name: 'CompanyCockpitPageUsers',
    component: CompanyCockpitPage,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: `/companies/:companyId/credits`,
    props: true,
    name: 'CompanyCockpitPageCredits',
    component: CompanyCockpitPage,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/requestoverview',
    name: 'Admin overview for all requests',
    component: AdminRequestsOverview,
    meta: {
      initialTabId: 'all-data-requests',
      requiresAuthentication: true,
    },
  },
  {
    path: `/qualityassurance`,
    name: 'UI for quality assurance',
    component: QualityAssurance,
    meta: {
      initialTabId: 'qa',
      requiresAuthentication: true,
    },
  },
  {
    path: `/qualityassurance/review/:datasetJudgementId`,
    name: 'DatasetReviewOverview',
    component: DatasetReviewOverview,
    props: (route: RouteLocationNormalized): { datasetJudgementId: string } => ({
      datasetJudgementId: route.params.datasetJudgementId as string,
    }),
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/datasets',
    name: 'Dataset Overview',
    component: DatasetOverview,
    meta: {
      initialTabId: 'my-datasets',
      requiresAuthentication: true,
    },
  },
  {
    path: '/requests',
    name: 'MyDataRequestsOverview',
    component: MyDataRequestsOverview,
    meta: {
      initialTabId: 'my-data-requests',
      requiresAuthentication: true,
    },
  },
  {
    path: `/requests/:requestId`,
    name: 'Data Request View Page',
    props: true,
    component: ViewDataRequestPage,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/singledatarequest/:companyId',
    name: 'Single Data Request',
    props: true,
    component: SingleDataRequest,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/portfolios',
    name: 'Portfolio Overview',
    component: PortfolioOverview,
    meta: {
      initialTabId: 'my-portfolios',
      requiresAuthentication: true,
    },
  },
  {
    path: '/shared-portfolios',
    name: 'Shared Portfolio Overview',
    component: SharedPortfolioOverview,
    meta: {
      initialTabId: 'shared-portfolios',
      requiresAuthentication: true,
    },
  },
  {
    path: '/api-key',
    name: 'ApiKeysPage',
    component: ApiKeysPage,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/unsubscribe/:subscriptionId',
    name: 'Unsubscribe from mails',
    props: true,
    component: UnsubscribeFromMailsPage,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: '/nocontent',
    name: 'NoContentFound',
    component: NoContentFound,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: '/platform-redirect',
    name: 'Platform Redirect',
    component: PlatformRedirect,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: KeycloakRedirect,
    meta: {
      requiresAuthentication: false,
      register: false,
    },
  },
  {
    path: '/register',
    name: 'Register',
    component: KeycloakRedirect,
    meta: {
      requiresAuthentication: false,
      register: true,
    },
  },
  {
    path: '/:notFound(.*)',
    redirect: '/nocontent',
    meta: {
      requiresAuthentication: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { top: 0 };
  },
});

export { routes };
export default router;
